/**
 * Icon set for the app shell and pages, in the same hand as the landing icons
 * (src/components/Icons.tsx): 24-box, 1.8 stroke, round caps, currentColor by
 * default so CSS decides the colour.
 */

interface IconProps {
  size?: number;
  color?: string;
  strokeWidth?: number;
  className?: string;
}

const defaults = { size: 20, color: 'currentColor', strokeWidth: 1.8 };

function Svg({
  size = defaults.size,
  className,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      {children}
    </svg>
  );
}

/** Four rounded tiles — overview / dashboard. */
export function Grid(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <rect x="4" y="4" width="7" height="7" rx="2" stroke={color} strokeWidth={strokeWidth} />
      <rect x="13" y="4" width="7" height="7" rx="2" stroke={color} strokeWidth={strokeWidth} />
      <rect x="4" y="13" width="7" height="7" rx="2" stroke={color} strokeWidth={strokeWidth} />
      <rect x="13" y="13" width="7" height="7" rx="2" stroke={color} strokeWidth={strokeWidth} />
    </Svg>
  );
}

/** Document sheet with lines — documents / checklist. */
export function Doc(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <rect x="5" y="3" width="14" height="18" rx="2.5" stroke={color} strokeWidth={strokeWidth} />
      <path d="M9 8.5h6M9 12h6M9 15.5h3.5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}

/** Speech bubble — the assistant. */
export function Bubble(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path
        d="M4 6.5A2.5 2.5 0 016.5 4h11A2.5 2.5 0 0120 6.5v7a2.5 2.5 0 01-2.5 2.5H9.5L5.5 20 5.5 16H6.5A2.5 2.5 0 014 13.5v-7z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path d="M8.5 9h7M8.5 12h4.5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}

/** Scales — law watch. */
export function Scales(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path d="M12 4v16M8 20h8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M5 7h14" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M5 7l-2.4 5.2a2.8 2.8 0 005.6 0L5.8 7" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />
      <path d="M18.9 7l-2.4 5.2a2.8 2.8 0 005.6 0L19.7 7" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />
    </Svg>
  );
}

/** Folder — cases. */
export function Folder(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path
        d="M3.5 7A2.5 2.5 0 016 4.5h3.2c.7 0 1.36.3 1.83.81l.94 1.05c.28.32.69.5 1.12.5H18A2.5 2.5 0 0120.5 9.5v7A2.5 2.5 0 0118 19H6a2.5 2.5 0 01-2.5-2.5V7z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Buildings — portfolio. */
export function Buildings(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path d="M3.5 20h17" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <path d="M5.5 20V6.5A1.5 1.5 0 017 5h5a1.5 1.5 0 011.5 1.5V20" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />
      <path d="M13.5 10H17a1.5 1.5 0 011.5 1.5V20" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" />
      <path d="M8 8.5h3M8 12h3M8 15.5h3" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}

/** Gear — settings. */
export function Gear(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="3" stroke={color} strokeWidth={strokeWidth} />
      <path
        d="M12 3.5l1 2.1 2.3-.5 1 1.7 2.1 1-.5 2.3 1.6 1.9-1.6 1.9.5 2.3-2.1 1-1 1.7-2.3-.5-1 2.1-1-2.1-2.3.5-1-1.7-2.1-1 .5-2.3L4.5 12l1.6-1.9-.5-2.3 2.1-1 1-1.7 2.3.5z"
        stroke={color}
        strokeWidth={strokeWidth * 0.85}
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Person — experts. */
export function Person(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <circle cx="12" cy="8" r="3.4" stroke={color} strokeWidth={strokeWidth} />
      <path d="M5.5 20c0-3.3 2.9-5.5 6.5-5.5s6.5 2.2 6.5 5.5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}

/** Tick. */
export function Tick(props: IconProps) {
  const { color = defaults.color, strokeWidth = 2.4 } = props;
  return (
    <Svg {...props}>
      <path d="M20 6L9 17l-5-5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Alert triangle. */
export function Alert(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path
        d="M10.7 4.6a1.5 1.5 0 012.6 0l7 12.4a1.5 1.5 0 01-1.3 2.2H5a1.5 1.5 0 01-1.3-2.2l7-12.4z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path d="M12 9.5v4" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <circle cx="12" cy="16.2" r="0.4" fill={color} stroke={color} strokeWidth="1" />
    </Svg>
  );
}

/** Clock. */
export function Clock(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8" stroke={color} strokeWidth={strokeWidth} />
      <path d="M12 8v4.2l2.8 1.7" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Padlock. */
export function Padlock(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <rect x="5" y="11" width="14" height="9" rx="2" stroke={color} strokeWidth={strokeWidth} />
      <path d="M8 11V8a4 4 0 018 0v3" stroke={color} strokeWidth={strokeWidth} />
    </Svg>
  );
}

/** Arrow right. */
export function ArrowRight(props: IconProps) {
  const { color = defaults.color, strokeWidth = 2.2 } = props;
  return (
    <Svg {...props}>
      <path d="M5 12h13M13 6l6 6-6 6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Chevron down (rotate in CSS for other directions). */
export function Chevron(props: IconProps) {
  const { color = defaults.color, strokeWidth = 2 } = props;
  return (
    <Svg {...props}>
      <path d="M6 9.5l6 6 6-6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Plus. */
export function Plus(props: IconProps) {
  const { color = defaults.color, strokeWidth = 2.2 } = props;
  return (
    <Svg {...props}>
      <path d="M12 5v14M5 12h14" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}

/** Upload tray. */
export function Upload(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path d="M12 15V4M7.5 8.5L12 4l4.5 4.5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.5 15.5v2A2.5 2.5 0 007 20h10a2.5 2.5 0 002.5-2.5v-2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}

/** Download — arrow into a tray. */
export function Download(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path d="M12 4v11M7.5 10.5L12 15l4.5-4.5" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.5 15.5v2A2.5 2.5 0 007 20h10a2.5 2.5 0 002.5-2.5v-2" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}

/** Swap / replace — two arrows cycling. */
export function Swap(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path d="M4 8h12l-3-3M20 16H8l3 3" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Send (paper-plane-ish arrow used by the landing chat). */
export function Send(props: IconProps) {
  const { color = defaults.color, strokeWidth = 2.4 } = props;
  return (
    <Svg {...props}>
      <path d="M5 12h13M13 6l6 6-6 6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Sparkle — AI marker. */
export function Sparkle(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path
        d="M12 4c.6 3.6 2.4 5.4 6 6-3.6.6-5.4 2.4-6 6-.6-3.6-2.4-5.4-6-6 3.6-.6 5.4-2.4 6-6z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path d="M18.5 15.5c.3 1.6 1.1 2.4 2.7 2.7-1.6.3-2.4 1.1-2.7 2.7-.3-1.6-1.1-2.4-2.7-2.7 1.6-.3 2.4-1.1 2.7-2.7z" fill={color} />
    </Svg>
  );
}

/** Shield tick — compliance / trust. */
export function Shield(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <path
        d="M12 3.5l7 2.6v5.2c0 4.3-2.9 7.4-7 9.2-4.1-1.8-7-4.9-7-9.2V6.1l7-2.6z"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />
      <path d="M8.8 12l2.2 2.2 4.2-4.4" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
    </Svg>
  );
}

/** Search magnifier. */
export function Search(props: IconProps) {
  const { color = defaults.color, strokeWidth = defaults.strokeWidth } = props;
  return (
    <Svg {...props}>
      <circle cx="11" cy="11" r="6.5" stroke={color} strokeWidth={strokeWidth} />
      <path d="M15.8 15.8L20 20" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}

/** Cross / close. */
export function Cross(props: IconProps) {
  const { color = defaults.color, strokeWidth = 2.2 } = props;
  return (
    <Svg {...props}>
      <path d="M6 6l12 12M18 6L6 18" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </Svg>
  );
}
